import { MailCheck, Send } from "lucide-react";
import { useNotify } from "ra-core";
import { useState } from "react";

import { formatDate } from "./gulaConstants";
import type { GulaOrder } from "./gulaTypes";
import { sendTrackingEmail } from "./installerNotify";

/**
 * "Enviado" card: whether the customer already has the tracking email (it goes
 * out by itself once the order is shipped and has a tracking number), with a
 * button to send it now or again.
 */
export const CustomerTrackingLine = ({
  order,
  onSent,
}: {
  order: GulaOrder;
  onSent: () => void;
}) => {
  const notify = useNotify();
  const [sending, setSending] = useState(false);
  const hasTracking = !!order.tracking_number?.trim();

  const send = async () => {
    setSending(true);
    try {
      const result = await sendTrackingEmail(order.id);
      notify(`Seguimiento enviado a ${result.to}`, { type: "success" });
      onSent();
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "No se ha podido enviar",
        { type: "error" },
      );
    } finally {
      setSending(false);
    }
  };

  const action = (label: string) => (
    <button
      type="button"
      onClick={send}
      disabled={sending}
      className="font-medium text-foreground hover:underline disabled:opacity-50"
    >
      {sending ? "Enviando…" : label}
    </button>
  );

  if (!order.email) {
    return (
      <p className="text-xs text-amber-700">
        El cliente no tiene email: pásale el seguimiento por WhatsApp.
      </p>
    );
  }
  if (order.tracking_emailed_at) {
    return (
      <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
        <MailCheck className="h-3 w-3 text-green-600" />
        Seguimiento enviado al cliente {formatDate(
          order.tracking_emailed_at,
        )} · {action("Reenviar")}
      </p>
    );
  }
  if (!hasTracking) {
    return (
      <p className="text-xs text-muted-foreground">
        El cliente recibirá el seguimiento por email en cuanto haya número.
      </p>
    );
  }
  return (
    <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      <Send className="h-3 w-3" />
      El seguimiento sale solo al cliente ({order.email}) ·{" "}
      {action("Enviar ahora")}
    </p>
  );
};
